'use client';

import posthog from 'posthog-js';
import { PostHogProvider as PostHogContextProvider } from 'posthog-js/react';
import { type ReactNode, useEffect, useRef } from 'react';
import { POSTHOG_INIT_OPTIONS, POSTHOG_KEY, isPostHogConfigured } from './config';
import { resolveIdentityTransition } from './identity';

type PostHogProviderProps = {
  children: ReactNode;
  /**
   * ログイン中ユーザーの顧客ID。ログアウト中は `null` / `undefined`。
   *
   * 各ログイン経路（メール / 集英社ID / 新規登録）を個別に修正すると漏れるため、
   * セッションを単一の真実として上位（アプリ側 Provider）から渡し、
   * ここで identify / reset の遷移を一元管理する。
   *
   * ⚠️ distinctId は顧客IDのみ。メール / 住所 / 電話などの PII は渡さない（GF-1889）。
   */
  distinctId?: string | null;
};

/**
 * PostHog の初期化と Context 提供を行う共通 Provider。
 *
 * - `NEXT_PUBLIC_POSTHOG_KEY` 未設定（テスト / 未構築環境）では init をスキップする（GF-1897）。
 * - ページビューは `capture_pageview: 'history_change'` で自動取得するため hook は不要。
 * - identify / reset は {@link PostHogProviderProps.distinctId} の変化に同期する。
 */
export const PostHogProvider = ({ children, distinctId }: PostHogProviderProps) => {
  const previousDistinctIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isPostHogConfigured() || posthog.__loaded) return;
    // POSTHOG_KEY は isPostHogConfigured() で存在を保証済み。
    posthog.init(POSTHOG_KEY as string, POSTHOG_INIT_OPTIONS);
  }, []);

  useEffect(() => {
    if (!posthog.__loaded) return;

    // 識別遷移の分岐は純関数に切り出してテスト可能にしている（identity.ts）。
    const action = resolveIdentityTransition(previousDistinctIdRef.current, distinctId);
    switch (action.type) {
      case 'resetThenIdentify':
        posthog.reset();
        // 匿名状態とログイン後を同一ユーザーとして紐付ける。PII は送らない。
        posthog.identify(action.distinctId);
        break;
      case 'identify':
        posthog.identify(action.distinctId);
        break;
      case 'reset':
        posthog.reset();
        break;
      case 'none':
        break;
    }

    previousDistinctIdRef.current = distinctId ?? null;
  }, [distinctId]);

  return <PostHogContextProvider client={posthog}>{children}</PostHogContextProvider>;
};
